import { Capacitor } from '@capacitor/core'

export type WrapperRole = 'helper' | 'member'

// Base package prefixes per wrapper role. Env-flavored builds append a
// suffix (`.sit`, `.prod`, …) via gradle applicationIdSuffix — treat any
// applicationId that starts with a base as that role so new envs don't
// need a code change here.
const ROLE_BY_APP_ID_PREFIX: ReadonlyArray<[string, WrapperRole]> = [
  ['com.timelining.helper.app', 'helper'],
  ['com.timelining.helper.stable', 'helper'],
  ['com.timelining.member.app', 'member'],
  ['com.timelining.member.stable', 'member'],
]

const resolveRoleFromAppId = (appId: string): WrapperRole | undefined => {
  for (const [prefix, role] of ROLE_BY_APP_ID_PREFIX) {
    if (appId === prefix || appId.startsWith(`${prefix}.`)) return role
  }
  return undefined
}

declare global {
  interface Window {
    __TIMELINING_WRAPPER_ROLE__?: WrapperRole
  }
}

// Set once during main.tsx boot from the async Capacitor App plugin. Read
// synchronously everywhere else via getWrapperRole so components can gate UI
// (e.g. hiding Google Sign-In in the helper APK) without their own async race.
export const setWrapperRoleFromAppId = (appId: string | undefined): void => {
  if (typeof window === 'undefined') return
  if (!appId) return
  const role = resolveRoleFromAppId(appId)
  if (role) window.__TIMELINING_WRAPPER_ROLE__ = role
}

export const getWrapperRole = (): WrapperRole | null => {
  if (typeof window === 'undefined') return null
  if (window.__TIMELINING_WRAPPER_ROLE__) return window.__TIMELINING_WRAPPER_ROLE__
  const isLocalhost = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost'
  if (!isLocalhost) return null
  const debugQueryRole = new URLSearchParams(window.location.search).get('wrapperRole')
  if (debugQueryRole === 'helper' || debugQueryRole === 'member') {
    try {
      window.localStorage.setItem('timelining-debug-wrapper-role', debugQueryRole)
    } catch {
      // ignore
    }
    return debugQueryRole
  }
  try {
    const debugRole = window.localStorage.getItem('timelining-debug-wrapper-role')
    return debugRole === 'helper' || debugRole === 'member' ? debugRole : null
  } catch {
    return null
  }
}

export const isNativeWrapper = (): boolean => Capacitor.isNativePlatform()
